import {
  MouseEvent,
  PropsWithChildren,
  useRef,
  ButtonHTMLAttributes,
} from 'react'

import * as S from './styles'

type Props = {
  /**
   * Function to be executed when the button is clicked
   * @example
   * ```tsx
   * <Button onClick={() => console.log('clicked')} />
   * ```
   * */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void

  /**
   * Class name of the button
   * @example
   * ```tsx
   * <Button className="button" />
   * ```
   * */
  className?: string
  disabled?: boolean
  width?: number
} & ButtonHTMLAttributes<HTMLButtonElement>
export type ButtonProps = PropsWithChildren<Omit<Props, 'children'>>

export const Button: React.FC<ButtonProps> = ({
  children,
  className,
  onClick,
  disabled,
  width,
  ...rest
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Remove effects
  const handleMouseLeave = async () => {
    const target = buttonRef.current

    if (!target || disabled) return

    target.style.transform = `unset`
    target.style.boxShadow = `0px 0px rgba(0,0,0,0)`
  }

  // Parallax effect
  const handleMouseMove = (e: MouseEvent) => {
    const container = containerRef.current
    const target = buttonRef.current

    if (!target || !container || disabled) return

    const rect = container.getBoundingClientRect()

    const mouseX = e.clientX
    const mouseY = e.clientY

    const centerX = rect.left + container.clientWidth / 2
    const centerY = rect.top + rect.height / 2

    const percentX = mouseX - rect.left - rect.width / 2
    const percentY = mouseY - rect.top - rect.height / 2

    const perspective = `perspective(${container.clientWidth}px)`
    const rotateX = `rotateX(${percentY / -1}deg)`
    const rotateY = `rotateY(${percentX / 10}deg)`
    const scale = 'scale(1.05)'

    target.style.transform = `${perspective} ${rotateY} ${rotateX} ${scale}`

    const blurRadius = '38px'
    const offsetX = `${(centerX - mouseX) * -0.05}px`
    const offsetY = `${(centerY - mouseY) * -0.05}px`
    target.style.boxShadow = `${offsetX} ${offsetY} ${blurRadius} rgba(9, 122, 115, 0.4) `
  }

  return (
    <S.ButtonContainer
      ref={containerRef}
      onMouseLeave={handleMouseLeave}
      onMouseMove={(e) => handleMouseMove(e)}
      className={className}
      disabled={disabled}
    >
      <S.Button
        ref={buttonRef}
        className="button"
        onClick={onClick}
        disabled={disabled}
        width={width}
        {...rest}
      >
        {children}
      </S.Button>
    </S.ButtonContainer>
  )
}
